import React, { useMemo } from 'react';
import { modalStyles as styles } from './modalStyles';
import { useAuth } from '@clerk/clerk-react';
import SubmissionImageGallery from './SubmissionImageGallery';
import RmaCustomerSummary from './RmaCustomerSummary';
import CloseCaseAction from './CloseCaseAction';
import {
  optionLabel,
  normalizeExpectedComposition,
  accessoriesIncluded,
  SHIPMENT_TYPE_OPTIONS,
  MOUNT_TYPE_OPTIONS,
  ACCESSORY_FIELDS
} from '../rmaFieldUtils';
import { colors } from '../theme';
import RmaDeviceList from './RmaDeviceList.jsx';
import { locationLabel } from '../utils/rmaLocation';

const API_BASE = process.env.REACT_APP_API_URL;

export default function RmaModalAssigned({ rma, onClose, onSave }) {
  const { getToken } = useAuth();
  const expectedNormalized = useMemo(
    () => normalizeExpectedComposition(rma.expectedReturn || {}),
    [rma.expectedReturn]
  );
  const expectedMount = expectedNormalized.mounted ? expectedNormalized.mount : null;
  const expectedAccessories = expectedNormalized.accessories || {};
  const expectedHasAccessories = accessoriesIncluded(expectedAccessories);
  const customerShipment =
    rma.customerShipment && rma.customerShipment.trackingNumber ? rma.customerShipment : null;

  const handleConfirm = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status: 'received'
        }),
      });
      const data = await res.json();
      onSave(data);
    } catch (err) {
      console.error('Failed to confirm received', err);
    }
  };

  const formatShipmentDate = value => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString();
    } catch (err) {
      return value;
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2>Confirm Arrival</h2>
        <RmaCustomerSummary rma={rma} />
        <p><strong>Assigned Location:</strong> {locationLabel(rma)}</p>
        <p><strong>Serial Number:</strong> {rma.serialNumber}</p>
        <RmaDeviceList rma={rma} title="All units expected in this shipment" />

        <div
          style={{
            marginTop: '1rem',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '0.75rem',
            backgroundColor: colors.sectionBackground,
            color: colors.background,
            textAlign: 'left'
          }}
        >
          <h3 style={{ margin: '0 0 0.5rem' }}>Expected shipment</h3>
          <p style={{ margin: '0 0 0.25rem' }}>
            <strong>Device Qty:</strong> {expectedNormalized.deviceQuantity || 1} pc
          </p>
          <p style={{ margin: '0 0 0.25rem' }}>
            <strong>Shipment type:</strong>{' '}
            {optionLabel(SHIPMENT_TYPE_OPTIONS, expectedNormalized.shipmentType)}
          </p>
          <p style={{ margin: '0 0 0.25rem' }}>
            <strong>Mounting:</strong>{' '}
            {expectedMount
              ? `${optionLabel(MOUNT_TYPE_OPTIONS, expectedMount.type)} (HMI ${expectedMount.hmi ? 'yes' : 'no'}) x ${expectedMount.qty}`
              : 'Not included'}
          </p>
          <div style={{ margin: '0 0 0.25rem' }}>
            <strong>Accessories:</strong>{' '}
            {expectedHasAccessories ? (
              <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem' }}>
                {ACCESSORY_FIELDS.filter(({ key }) => Number(expectedAccessories[key] || 0) > 0).map(({ key, label }) => (
                  <li key={key}>
                    {label} x {expectedAccessories[key]}
                  </li>
                ))}
              </ul>
            ) : (
              <span>None specified</span>
            )}
          </div>
        </div>

        <div
          style={{
            marginTop: '1rem',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '0.75rem',
            backgroundColor: 'rgba(255,255,255,0.05)',
            textAlign: 'left'
          }}
        >
          <h3 style={{ margin: '0 0 0.5rem' }}>Customer shipment</h3>
          {customerShipment ? (
            <ul style={{ margin: 0, paddingLeft: '1.15rem', fontSize: '0.95rem' }}>
              <li>
                <strong>Tracking:</strong> {customerShipment.trackingNumber}
              </li>
              {customerShipment.carrier && (
                <li>
                  <strong>Carrier:</strong> {customerShipment.carrier}
                </li>
              )}
              {customerShipment.comment && (
                <li>
                  <strong>Comment:</strong> {customerShipment.comment}
                </li>
              )}
              {customerShipment.shippedAt && (
                <li>
                  Submitted {formatShipmentDate(customerShipment.shippedAt)}
                </li>
              )}
            </ul>
          ) : (
            <p style={{ margin: 0, fontSize: '0.9rem' }}>No tracking number submitted by customer.</p>
          )}
        </div>

        {(rma.description || rma.faultType || rma.customerExpectation || rma.warrantyStatus) && (
          <div
            style={{
              marginTop: '1rem',
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '0.75rem',
              backgroundColor: 'rgba(255,255,255,0.05)',
              textAlign: 'left'
            }}
          >
            <h3 style={{ margin: '0 0 0.5rem' }}>Fault & expectations</h3>
            {rma.faultType && (
              <p style={{ margin: '0 0 0.25rem' }}>
                <strong>Fault type:</strong> {rma.faultType}
              </p>
            )}
            {rma.warrantyStatus && (
              <p style={{ margin: '0 0 0.25rem' }}>
                <strong>Warranty status:</strong> {rma.warrantyStatus}
              </p>
            )}
            {rma.customerExpectation && (
              <p style={{ margin: '0 0 0.25rem' }}>
                <strong>Customer expectation:</strong> {rma.customerExpectation}
              </p>
            )}
            {rma.description && (
              <p style={{ margin: '0 0 0.25rem' }}>
                <strong>Description:</strong> {rma.description}
              </p>
            )}
          </div>
        )}

        {rma.customerNotes && rma.customerNotes.length > 0 && (
          <div
            style={{
              marginTop: '1rem',
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '0.75rem',
              backgroundColor: 'rgba(255,255,255,0.05)',
              textAlign: 'left'
            }}
          >
            <h3 style={{ margin: '0 0 0.5rem' }}>Customer communication</h3>
            {rma.customerNotes.map((note, i) => (
              <div
                key={i}
                style={{
                  borderTop: i > 0 ? `1px solid ${colors.border}` : 'none',
                  paddingTop: i > 0 ? '0.5rem' : 0,
                  marginTop: i > 0 ? '0.5rem' : 0
                }}
              >
                <p style={{ margin: '0 0 0.25rem', fontSize: '0.85rem', opacity: 0.7 }}>
                  {note.submittedBy} — {new Date(note.createdAt).toLocaleString()}
                </p>
                <p style={{ margin: 0 }}>{note.message}</p>
              </div>
            ))}
          </div>
        )}

        <SubmissionImageGallery images={rma.submissionImages} rmaId={rma._id} canCategorize={true} />

        <div style={{ marginTop: '1rem' }}>
          <button onClick={handleConfirm} style={styles.button}>Confirm Received</button>
          <button onClick={onClose} style={styles.buttonAlt}>Cancel</button>
        </div>
        <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
          <CloseCaseAction rma={rma} onSave={onSave} />
        </div>
      </div>
    </div>
  );
}

